import {
  ArrowDownFromLine,
  Download,
  Flag,
  Group,
  Hammer,
  Hourglass,
  Pointer,
  Swords,
  Upload,
  X,
} from "lucide-react";
import type { ReactNode } from "react";

import { CoinMoneyIcon } from "#/components/game/CoinMoneyIcon";
import { useBoardActionHint } from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";
import type { BoardActionHint } from "#/game/selection/actionHint";
import type { SelectionState } from "#/game/selection/types";
import { useCoarsePointer } from "#/hooks/useCoarsePointer";
import { cn } from "#/lib/utils";

const ICON_CLASS = "size-2.5 fill-current";
const SELECT_COLOR = "#475569";

const UNLOAD_COLOR = "#0369a1";
const WAIT_COLOR = "#d97706";

function isMobileProminentHint(hint: BoardActionHint): boolean {
  return (
    hint.kind === "capture" ||
    hint.kind === "join" ||
    hint.kind === "load" ||
    hint.kind === "wait" ||
    hint.kind === "unload-here" ||
    hint.kind === "unload-select" ||
    hint.kind === "unload-or-wait"
  );
}

function shouldShowMobileBar(hint: BoardActionHint, selection: SelectionState): boolean {
  if (hint.kind === "wait" && selection.mode !== "move-preview") return false;
  if (isMobileProminentHint(hint)) return true;
  if (selection.mode === "transport-unload" && hint.kind !== "cancel") return true;
  return false;
}

const HINT_META: Record<
  Exclude<BoardActionHint["kind"], "none">,
  { icon: ReactNode | null; color: string; label: string | null }
> = {
  move: {
    icon: <ArrowDownFromLine className={ICON_CLASS} />,
    color: "#2563eb",
    label: "Move here",
  },
  "move-to-join": {
    icon: <ArrowDownFromLine className={ICON_CLASS} />,
    color: "#2563eb",
    label: "Move here to combine units",
  },
  wait: { icon: <Hourglass className={ICON_CLASS} />, color: WAIT_COLOR, label: "Wait here" },
  build: { icon: <Hammer className={ICON_CLASS} />, color: "#7c3aed", label: "Build unit" },
  load: { icon: <Upload className={ICON_CLASS} />, color: "#0369a1", label: "Load into transport" },
  "unload-here": {
    icon: <Download className={ICON_CLASS} />,
    color: UNLOAD_COLOR,
    label: "Unload here",
  },
  "unload-select": {
    icon: <Download className={ICON_CLASS} />,
    color: UNLOAD_COLOR,
    label: "Choose a tile to unload",
  },
  "unload-or-wait": {
    icon: null,
    color: UNLOAD_COLOR,
    label: "Choose unload tile, or wait here",
  },
  attack: { icon: <Swords className={ICON_CLASS} />, color: "#dc2626", label: "Attack" },
  capture: { icon: <Flag className={ICON_CLASS} />, color: "#16a34a", label: "Capture" },
  join: { icon: <Group className={ICON_CLASS} />, color: "#0f766e", label: "Combine units" },
  cancel: {
    icon: <X className="size-2.5" strokeWidth={3} />,
    color: "#64748b",
    label: "Click to cancel",
  },
  "select-unit": {
    icon: <Pointer className="size-2.5" strokeWidth={2.25} />,
    color: SELECT_COLOR,
    label: null,
  },
};

function ActionBadge({ icon, color }: { icon: ReactNode; color: string }) {
  return (
    <span
      className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-white ring-1 ring-white/80"
      style={{ backgroundColor: color }}
      aria-hidden
    >
      {icon}
    </span>
  );
}

function JoinDetail({
  resultHp,
  refund,
}: Pick<Extract<BoardActionHint, { kind: "join" }>, "resultHp" | "refund">) {
  return (
    <span className="inline-flex items-center gap-1.5 tabular-nums">
      <span>→ {resultHp}%</span>
      {refund > 0 && (
        <span className="inline-flex items-center gap-0.5 font-medium text-foreground">
          +{refund}
          <CoinMoneyIcon size="sm" className="ml-1" />
        </span>
      )}
    </span>
  );
}

function hintDetail(hint: BoardActionHint): ReactNode {
  switch (hint.kind) {
    case "attack":
      return <span className="tabular-nums">{hint.forecast}%</span>;
    case "capture":
      return (
        <span className="tabular-nums">
          → {hint.to}/{hint.full}
        </span>
      );
    case "join":
      return <JoinDetail resultHp={hint.resultHp} refund={hint.refund} />;
    default:
      return null;
  }
}

function HintBadges({ hint }: { hint: Exclude<BoardActionHint, { kind: "none" }> }) {
  if (hint.kind === "unload-or-wait") {
    return (
      <>
        <ActionBadge icon={<Download className={ICON_CLASS} />} color={UNLOAD_COLOR} />
        <ActionBadge icon={<Hourglass className={ICON_CLASS} />} color={WAIT_COLOR} />
      </>
    );
  }

  const meta = HINT_META[hint.kind];
  return <ActionBadge icon={meta.icon} color={meta.color} />;
}

function StatusBarContent({ hint }: { hint: BoardActionHint }) {
  if (hint.kind === "none") {
    return <span className="text-muted-foreground">Select a unit to act</span>;
  }

  const meta = HINT_META[hint.kind];
  const label = hint.kind === "select-unit" ? `Select ${hint.name}` : meta.label;

  return (
    <>
      <HintBadges hint={hint} />
      <span className="text-foreground">{label}</span>
      <span className="text-muted-foreground">{hintDetail(hint)}</span>
    </>
  );
}

export function BoardActionStatusBar() {
  const coarsePointer = useCoarsePointer();
  const selection = useGameSelector((s) => s.selection);
  const hint = useBoardActionHint();
  const showMobileBar = shouldShowMobileBar(hint, selection);

  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-200 ease-out",
        showMobileBar ? "grid-rows-[1fr]" : "grid-rows-[0fr] pointer-fine:grid-rows-[1fr]",
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          role="status"
          aria-live="polite"
          aria-hidden={coarsePointer && !showMobileBar}
          className={cn(
            "flex min-h-9 items-center justify-center gap-2 border-t border-border px-2 sm:px-3 text-xs transition-[transform,opacity] duration-200 ease-out",
            showMobileBar
              ? "translate-y-0 opacity-100"
              : "translate-y-full opacity-0 pointer-fine:translate-y-0 pointer-fine:opacity-100",
          )}
        >
          <StatusBarContent hint={hint} />
        </div>
      </div>
    </div>
  );
}
